import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { filter } from 'rxjs/operators';
import { MedicalExamCertificateMegaMenuModalComponent } from '../../../../driver/credential/issuance/requirement/medical-exam-certificate-megamenu-modal.component';
import { IncidentConfirmationService } from '../../../../driver/services/incident-confirmation/incident-confirmation.service';
import { SourceSystemType } from '../../../../foundation/models/enums/source-system-type.enum';
import { CustomerPurgeWindow } from '../../../../purge/customer-purge.modal.component';
import { AuxRoute } from '../../../models/route/aux-route.model';
import { SecurityTransactionMenuItem } from '../../../models/security-transaction-menu-item/security-transaction-menu-item.model';
import { MvdModalService } from '../../../services/modal/mvd-modal.service';
import { RouteBuilderFactory } from '../../../services/route-builder/route-builder-factory.service';
import { Router } from '@angular/router';
import { DeleteCustomerIdentificationWindow } from 'app/purge/delete-customer-identification.modal.component';
import { FormBuilder, UntypedFormGroup } from '@angular/forms';

@Component({
    selector: 'mega-menu',
    templateUrl: './mega-menu.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MegaMenuComponent implements OnInit {
    private _menuItems: SecurityTransactionMenuItem[];

    @Input() currentMegaMenuIndex: number;
    @Input() set menuItems(value: SecurityTransactionMenuItem[]) {
        this._menuItems = value;
        this.filteredMenuItems = this.filterMenuItems(this.menuItems);
    }

    get menuItems(): SecurityTransactionMenuItem[] {
        return this._menuItems;
    }

    @Output() onShowMegaMenuToggle = new EventEmitter<any>();
    @Output() onSelectRoute = new EventEmitter<any>();
    @Output() onSelectService = new EventEmitter<number>();
    @Output() onStartTransaction = new EventEmitter<any>();

    public currentMegaMenuIndexToggle = false;
    private sourceSystemTypes: SourceSystemType = new SourceSystemType();

    public filterForm: UntypedFormGroup;
    public filteredMenuItems: SecurityTransactionMenuItem[];

    constructor(
        private incidentConfirmationService: IncidentConfirmationService,
        private modalService: MvdModalService,
        private routeBuilderFactory: RouteBuilderFactory,
        private router: Router,
        private fb: FormBuilder
    ) { }

    ngOnInit() {
        this.filterForm = this.fb.group({
            'keyword': ['']
        });

        this.filterForm.get('keyword').valueChanges.subscribe(() => {
            this.filteredMenuItems = this.filterMenuItems(this.menuItems);
        });

        this.filteredMenuItems = this.menuItems;
    }

    functionRequest(methodName: string) {
        // check if method exists and call it
        if (this[methodName]) {
            this[methodName]();
        }
    }

    megaMenuItemClicked(event: MouseEvent): void {
        try {
            let index = parseInt((event.currentTarget as any).attributes['data-index'].value);
            this.currentMegaMenuIndex = this.currentMegaMenuIndex == index ? -999 : index;
            this.currentMegaMenuIndexToggle = true;
            this.onShowMegaMenuToggle.emit();
        } catch (err) {
            console.log('menu error');
        }
    }

    routeSelected() {
        this.onSelectRoute.emit();
    }

    selectedService(id: number) {
        this.onSelectService.emit(id);
    }

    keyword() {
        return this.filterForm?.get('keyword').value;
    }

    clearKeyword() {
        this.filterForm.get('keyword').patchValue('');
    }

    private filterMenuItems(menuItems: SecurityTransactionMenuItem[]): SecurityTransactionMenuItem[] {
        if (!this.keyword()) {
            return menuItems;
        }

        var matchingMenuItems = [];
        menuItems?.forEach(menuItem => {
            if (menuItem.securityTransactionTypeMenuItemDisplayValue.toLocaleLowerCase().includes(this.keyword().toLocaleLowerCase())) {
                matchingMenuItems.push(menuItem);

            } else if (menuItem.children?.length > 0) {
                let matchingChildren = this.filterMenuItems(menuItem.children);

                if (matchingChildren?.length > 0) {
                    let menuItemClone = Object.assign({}, menuItem) as SecurityTransactionMenuItem;
                    menuItemClone.children = matchingChildren;
                    matchingMenuItems.push(menuItemClone);
                }
            }
        });

        return matchingMenuItems;
    }

    // below methods are called by functionRequest

    private accommodativeDataTitle() {
        const routeBuilder = this.routeBuilderFactory.create(this.sourceSystemTypes.TitleRegistration.id);

        const auxRoutes: AuxRoute[] = new Array<AuxRoute>();
        const bodyRoute = new AuxRoute();
        bodyRoute.routeName = 'accommodative-data-search-title';
        bodyRoute.outletName = 'body';
        bodyRoute.routeParameter = [-1];
        bodyRoute.isModule = true;

        auxRoutes.push(bodyRoute);
        const outlets = routeBuilder.buildAuxiliaryRouteCommand(auxRoutes);
        this.router.navigate(['/titleregistration/standalone', outlets]);
    }

    private estimateCreditInquiry() {
        const routeBuilder = this.routeBuilderFactory.create(this.sourceSystemTypes.TitleRegistration.id);

        const auxRoutes: AuxRoute[] = new Array<AuxRoute>();
        const bodyRoute = new AuxRoute();
        bodyRoute.routeName = 'estimate-registration-credit';
        bodyRoute.outletName = 'body';
        bodyRoute.routeParameter = [-1];
        bodyRoute.isModule = true;

        auxRoutes.push(bodyRoute);
        const outlets = routeBuilder.buildAuxiliaryRouteCommand(auxRoutes);
        this.router.navigate(['/titleregistration/standalone', outlets]);
    }

    private estimateRegistrationFeeInquiry() {
        const routeBuilder = this.routeBuilderFactory.create(this.sourceSystemTypes.TitleRegistration.id);

        const auxRoutes: AuxRoute[] = new Array<AuxRoute>();
        const bodyRoute = new AuxRoute();
        bodyRoute.routeName = 'estimate-registration-fee';
        bodyRoute.outletName = 'body';
        bodyRoute.routeParameter = [-1];
        bodyRoute.isModule = true;

        auxRoutes.push(bodyRoute);
        const outlets = routeBuilder.buildAuxiliaryRouteCommand(auxRoutes);
        this.router.navigate(['/titleregistration/standalone', outlets]);
    }

    private bulkRegistrationCreditRequest() {
        const routeBuilder = this.routeBuilderFactory.create(this.sourceSystemTypes.TitleRegistration.id);

        const auxRoutes: AuxRoute[] = new Array<AuxRoute>();
        const bodyRoute = new AuxRoute();
        bodyRoute.routeName = 'bulk-registration-credit';
        bodyRoute.outletName = 'body';
        bodyRoute.routeParameter = [-1];
        bodyRoute.isModule = true;

        auxRoutes.push(bodyRoute);
        const outlets = routeBuilder.buildAuxiliaryRouteCommand(auxRoutes);
        this.router.navigate(['/titleregistration/standalone', outlets]);
    }

    private eTitleTransferLogInquiry() {
        const routeBuilder = this.routeBuilderFactory.create(this.sourceSystemTypes.TitleRegistration.id);

        const auxRoutes: AuxRoute[] = new Array<AuxRoute>();
        const bodyRoute = new AuxRoute();
        bodyRoute.routeName = 'etitletransfer-inquiry';
        bodyRoute.outletName = 'body';
        bodyRoute.routeParameter = [-1];
        bodyRoute.isModule = true;

        auxRoutes.push(bodyRoute);
        const outlets = routeBuilder.buildAuxiliaryRouteCommand(auxRoutes);
        this.router.navigate(['/titleregistration/standalone', outlets]);
    }

    private medicalExamCertificate() {
        this.modalService.open(MedicalExamCertificateMegaMenuModalComponent)
            .afterClosed()
            .pipe(filter(result => !!result))
            .subscribe(r => this.incidentConfirmationService.confirmIncidentRedirect(null, r));

        this.routeSelected();
    }

    private purgeCustomer() {
        this.modalService.open(CustomerPurgeWindow).afterClosed()
            .pipe(filter(result => !!result))
            .subscribe(result => this.onStartTransaction.emit(result));
    }

    private searchFleet() {
        const routeBuilder = this.routeBuilderFactory.create(this.sourceSystemTypes.TitleRegistration.id);

        const auxRoutes: AuxRoute[] = new Array<AuxRoute>();
        const bodyRoute = new AuxRoute();
        bodyRoute.routeName = 'fleet-search';
        bodyRoute.outletName = 'body';
        bodyRoute.routeParameter = [-1];
        bodyRoute.isModule = true;

        auxRoutes.push(bodyRoute);
        const outlets = routeBuilder.buildAuxiliaryRouteCommand(auxRoutes);
        this.router.navigate(['/titleregistration/standalone', outlets]);
    }

    private searchInsurance() {
        const routeBuilder = this.routeBuilderFactory.create(this.sourceSystemTypes.TitleRegistration.id);

        const auxRoutes: AuxRoute[] = new Array<AuxRoute>();
        const bodyRoute = new AuxRoute();
        bodyRoute.routeName = 'insurance-policy-search';
        bodyRoute.routeParameter = ['policy'];
        bodyRoute.outletName = 'body';
        bodyRoute.isModule = true;

        auxRoutes.push(bodyRoute);
        const outlets = routeBuilder.buildAuxiliaryRouteCommand(auxRoutes);

        this.router.navigate(['/titleregistration/standalone', outlets]);
    }

    private searchInsuranceCompany() {
        const routeBuilder = this.routeBuilderFactory.create(this.sourceSystemTypes.TitleRegistration.id);

        const auxRoutes: AuxRoute[] = new Array<AuxRoute>();
        const bodyRoute = new AuxRoute();
        bodyRoute.routeName = 'insurance-company-search';
        bodyRoute.outletName = 'body';
        bodyRoute.routeParameter = [-1];
        bodyRoute.isModule = true;

        auxRoutes.push(bodyRoute);
        const outlets = routeBuilder.buildAuxiliaryRouteCommand(auxRoutes);
        this.router.navigate(['/titleregistration/standalone', outlets]);
    }

    private omegaRegistrationRenewal() {
        const routeBuilder = this.routeBuilderFactory.create(this.sourceSystemTypes.TitleRegistration.id);

        const auxRoutes: AuxRoute[] = new Array<AuxRoute>();
        const bodyRoute = new AuxRoute();
        bodyRoute.routeName = 'omega-registration-renewal';
        bodyRoute.outletName = 'body';
        bodyRoute.routeParameter = [-1];
        bodyRoute.isModule = true;

        auxRoutes.push(bodyRoute);
        const outlets = routeBuilder.buildAuxiliaryRouteCommand(auxRoutes);
        this.router.navigate(['/titleregistration/standalone', outlets]);
    }
    private searchPermit() {
        const routeBuilder = this.routeBuilderFactory.create(this.sourceSystemTypes.TitleRegistration.id);

        const auxRoutes: AuxRoute[] = new Array<AuxRoute>();
        const bodyRoute = new AuxRoute();
        bodyRoute.routeName = 'permit-search';
        bodyRoute.outletName = 'body';
        bodyRoute.routeParameter = [-1];
        bodyRoute.isModule = true;

        auxRoutes.push(bodyRoute);
        const outlets = routeBuilder.buildAuxiliaryRouteCommand(auxRoutes);
        this.router.navigate(['/titleregistration/standalone', outlets]);
    }

    private titleCancelOOS() {
        const routeBuilder = this.routeBuilderFactory.create(this.sourceSystemTypes.TitleRegistration.id);

        const auxRoutes: AuxRoute[] = new Array<AuxRoute>();
        const bodyRoute = new AuxRoute();
        bodyRoute.routeName = 'canceltitle';
        bodyRoute.outletName = 'body';
        bodyRoute.routeParameter = [-1];
        bodyRoute.isModule = true;

        auxRoutes.push(bodyRoute);
        const outlets = routeBuilder.buildAuxiliaryRouteCommand(auxRoutes);
        this.router.navigate(['/titleregistration/standalone', outlets]);
    }

    private vehicleHistoryDetail() {
        const routeBuilder = this.routeBuilderFactory.create(this.sourceSystemTypes.TitleRegistration.id);

        const auxRoutes: AuxRoute[] = new Array<AuxRoute>();
        const bodyRoute = new AuxRoute();
        bodyRoute.routeName = 'vehicle-title-history';
        bodyRoute.outletName = 'body';
        bodyRoute.routeParameter = [-1];
        bodyRoute.isModule = true;

        auxRoutes.push(bodyRoute);
        const outlets = routeBuilder.buildAuxiliaryRouteCommand(auxRoutes);
        this.router.navigate(['/titleregistration/standalone', outlets]);
    }

    private viewAuditReport() {
        this.router.navigate(['report', 'audit']);
    }

    private viewOigReport() {
        this.router.navigate(['report', 'secure']);
    }

    private deleteSSN() {
        this.modalService.open(DeleteCustomerIdentificationWindow).afterClosed()
            .pipe(filter(result => !!result))
            .subscribe(result => this.onStartTransaction.emit(result));
    }
}
